import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SiteLayout from "@/marketing/SiteLayout";
import { BRAND } from "@/lib/brand";

const UPDATED = "13 October 2026";

function Page({ title, children, testid }) {
    return (
        <SiteLayout>
            <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-14" data-testid={testid}>
                <div className="label-eyebrow text-indigo-300">Legal</div>
                <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-50 tracking-tight">{title}</h1>
                <p className="mt-2 text-xs text-slate-500">Last updated {UPDATED}</p>
                <div className="mt-8 space-y-6 text-sm leading-relaxed text-slate-300 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-100 [&_h2]:mt-10 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_a]:text-indigo-300 [&_a:hover]:text-indigo-200">
                    {children}
                </div>
            </article>
        </SiteLayout>
    );
}

const Mail = () => <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>;

export function Privacy() {
    return (
        <Page title="Privacy policy" testid="privacy-page">
            <p>
                {BRAND.name} is operated by {BRAND.company} ({BRAND.city}) — “we”, “us”. This policy explains
                what we collect when you use {BRAND.name}, why, and the choices you have. Questions: <Mail />.
            </p>

            <h2>What we collect</h2>
            <ul>
                <li><strong>Account details</strong> — your name, email address and a hashed password, or your Google account name and email if you sign in with Google.</li>
                <li><strong>Business details you give us or confirm</strong> — business name, city, description, address, phone, website and social profile links, brand voice, services and the claims you allow or ban.</li>
                <li><strong>Public information about your business</strong> — listings, web pages and public social profiles we look up to detect and analyse your online presence (for example through Google Places and web search providers).</li>
                <li><strong>Data from accounts you connect</strong>:
                    <ul>
                        <li>Instagram — your account ID and username, your posts' captions and links, and comments on your posts (commenter username and text), so you can moderate and reply.</li>
                        <li>WhatsApp Business — messages sent to your business number and the sender's phone number and profile name, so you can answer them.</li>
                        <li>Google Search Console and Google Analytics — aggregated search and traffic statistics for the site or property you choose.</li>
                    </ul>
                </li>
                <li><strong>Usage information</strong> — log data such as IP address, browser type and the actions you take in the app, used for security and to improve the product. Our hosting platform's analytics service (PostHog) may record page views and sessions.</li>
            </ul>

            <h2>How we use it</h2>
            <ul>
                <li>To detect and analyse your business's online presence and show you the results.</li>
                <li>To draft replies, fixes, reports and answers, and to publish them only according to the approval settings you choose.</li>
                <li>To operate, secure and support the service, and to contact you about your account.</li>
            </ul>
            <p>We do not sell personal information, and we do not use your data or your customers' messages to train AI models.</p>

            <h2>AI processing</h2>
            <p>
                Drafts and analyses are generated by third-party AI models (such as OpenAI and Google Gemini) called through
                their APIs. We send only what is needed for the task — for example the text of a review and your brand voice —
                and those providers process it under their API terms.
            </p>

            <h2>Google user data</h2>
            <p>
                {BRAND.name}'s use and transfer to any other app of information received from Google APIs will adhere to the{" "}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
                    Google API Services User Data Policy
                </a>
                , including the Limited Use requirements. We request read-only access to Search Console and Analytics, use it
                only to show you your own statistics inside {BRAND.name}, and do not use it for advertising.
            </p>

            <h2>Meta platform data</h2>
            <p>
                Data from Instagram and WhatsApp is used only to provide the features you turned on for your own business and
                is not shared with other customers. Disconnecting an account deletes its access token straight away; comments
                and messages already stored stay in your account until you ask us to delete them — see{" "}
                <Link to="/data-deletion">Data deletion</Link>. If you remove the app from Instagram, Meta tells us and we
                delete the access token; if you also ask Meta to delete your data, we delete that account's stored comments
                and events automatically.
            </p>

            <h2>Sharing</h2>
            <p>We share data only with service providers that help us run {BRAND.name} — hosting, database, email, AI model and search providers — under contracts that limit their use of it, or when the law requires it.</p>

            <h2>Security and retention</h2>
            <p>
                Access tokens for connected accounts are encrypted at rest and never shown in full in the app. We keep your data
                while your account is active. When you delete your account we delete your business data within 30 days, except
                where we must keep records by law.
            </p>

            <h2>Your choices</h2>
            <ul>
                <li>Disconnect Instagram, WhatsApp or Google from Settings → Integrations, or from the SEO page, at any time.</li>
                <li>Edit or remove business details from Company Profile.</li>
                <li>Ask for a copy of your data, a correction, or deletion by emailing <Mail />.</li>
            </ul>

            <h2>Changes</h2>
            <p>We'll post updates here and change the date above. For significant changes we'll tell you in the app or by email.</p>
        </Page>
    );
}

export function Terms() {
    return (
        <Page title="Terms of service" testid="terms-page">
            <p>
                These terms govern your use of {BRAND.name}, provided by {BRAND.company}. By creating an account you agree to
                them. If you use {BRAND.name} for a business, you confirm you're allowed to act for that business.
            </p>

            <h2>The service</h2>
            <p>
                {BRAND.name} helps you find and analyse your business's online profiles, and drafts replies, fixes and reports
                with AI. Some features need you to connect third-party accounts, and their availability depends on those
                platforms.
            </p>

            <h2>Your responsibilities</h2>
            <ul>
                <li>Keep your login details safe and tell us about any unauthorised use.</li>
                <li>Only connect accounts and businesses you own or are authorised to manage.</li>
                <li>Review what you approve. AI drafts can be wrong; you are responsible for content published on your accounts, including content published automatically under settings you chose.</li>
                <li>Follow the rules of the platforms you connect, including Meta's and Google's terms and policies, and applicable laws on advertising, privacy and messaging customers.</li>
                <li>Don't misuse the service — no spam, scraping, reverse engineering, or attempts to access other customers' data.</li>
            </ul>

            <h2>Your content</h2>
            <p>
                You keep ownership of your business information and content. You give us permission to process it only to
                provide {BRAND.name} to you.
            </p>

            <h2>Plans and payment</h2>
            <p>
                Paid plans, if you choose one, are billed as agreed with you in writing or on the pricing shown at sign-up.
                We'll give notice before changing prices for an active subscription.
            </p>

            <h2>Availability and changes</h2>
            <p>
                We work to keep {BRAND.name} available and accurate, but the service is provided “as is”. Scores, rankings and
                AI analyses are estimates, not guarantees of results. We may change or discontinue features, and will give
                reasonable notice of material changes.
            </p>

            <h2>Liability</h2>
            <p>
                To the extent the law allows, we are not liable for indirect or consequential losses, and our total liability
                is limited to the amount you paid us in the 12 months before the claim.
            </p>

            <h2>Ending your account</h2>
            <p>
                You can stop using {BRAND.name} and ask us to delete your account at any time. We may suspend accounts that
                break these terms. See <Link to="/data-deletion">Data deletion</Link> for what happens to your data.
            </p>

            <h2>Law</h2>
            <p>These terms are governed by the laws of India, and the courts of Ahmedabad, Gujarat have jurisdiction.</p>

            <h2>Contact</h2>
            <p><Mail /></p>
        </Page>
    );
}

function DeletionStatus({ code }) {
    const [state, setState] = useState(null);
    useEffect(() => {
        api.dataDeletionStatus(code)
            .then(setState)
            .catch(() => setState({ status: "not_found" }));
    }, [code]);
    if (!state) return null;
    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="deletion-status">
            <div className="label-eyebrow">Deletion request {code}</div>
            <div className="mt-1 text-slate-100">
                {state.status === "completed"
                    ? `Completed${state.completed_at ? ` on ${new Date(state.completed_at).toLocaleDateString()}` : ""} — the Instagram account's data has been deleted.`
                    : "We couldn't find this confirmation code. Check the link, or email us."}
            </div>
        </div>
    );
}

export function DataDeletion() {
    const [params] = useSearchParams();
    const code = (params.get("code") || "").slice(0, 64);
    return (
        <Page title="Data deletion" testid="data-deletion-page">
            {code && <DeletionStatus code={code} />}
            <p>You can remove the data {BRAND.name} holds about you or your business at any time.</p>

            <h2>Disconnect an account</h2>
            <ul>
                <li><strong>Instagram</strong> — Settings → Integrations → Instagram → Disconnect. The access token is deleted immediately and no new comments are collected. Comments already stored stay until you ask us to delete them.</li>
                <li><strong>WhatsApp Business</strong> — Settings → Integrations → WhatsApp → Disconnect. The access token and app secret are deleted immediately. Message history stays until you ask us to delete it.</li>
                <li><strong>Google Search Console / Analytics</strong> — SEO &amp; Visibility → Disconnect. Tokens are deleted immediately. You can also remove access from your Google account's security settings.</li>
            </ul>
            <p>
                You can also remove {BRAND.name} from Instagram directly: Instagram → Settings → Website permissions → Apps and
                websites. When Meta tells us you've removed the app, we delete the access token; when Meta sends a data deletion
                request, we delete the account, its stored comments and webhook events, and give Meta a confirmation code you
                can check below.
            </p>

            <h2>Delete everything</h2>
            <p>
                Email <Mail /> from the address on your account with the subject “Delete my data”, and tell us which business or
                account it's for. We'll confirm the request, then delete your account, business profiles, connected-account
                data (including stored Instagram comments and WhatsApp messages), drafts and reports within 30 days, and email
                you when it's done.
            </p>

            <h2>If you're a customer of a business using {BRAND.name}</h2>
            <p>
                If you commented on a business's Instagram or messaged it on WhatsApp and want that data removed, contact the
                business, or email us with the business name and your username or phone number and we'll pass the request on
                and remove it from {BRAND.name}.
            </p>
        </Page>
    );
}
